import React from 'react'
import styled from 'styled-components'

// Elements
import ParseContent from 'components/shared/ParseContent'

// Interface
import { TextProps } from 'components/flex/Text/TextShell'

const Section = styled.section`
  background-color: ${({ theme }) => theme.color.grey};
`

const MainTitle = styled.h2`
  color: ${({ theme }) => theme.color.primary};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  margin-bottom: 2.5rem;

  @media (min-width: 992px) {
    font-size: 36px;
    line-height: 1.25;
  }

  @media (max-width: 991.98px) {
    font-size: 26px;
    line-height: 1.3;
  }
`

const Card = styled.div`
  background-color: ${({ theme }) => theme.color.light};
  height: 100%;
  padding: 1.5rem;

  @media (min-width: 992px) {
    padding: 1.5rem;
  }
`

const CardTitle = styled.h3`
  color: ${({ theme }) => theme.color.primary};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-size: ${({ theme }) => theme.font.size.huge};
  font-weight: ${({ theme }) => theme.font.weight.extraBold};
  line-height: normal;
  margin-bottom: 0.75rem;

  @media (max-width: 991px) {
    font-size: ${({ theme }) => theme.font.size.big};
    margin-bottom: 0.5rem;
  }
`

const CardBody = styled(ParseContent)`
  color: ${({ theme }) => theme.color.primary};
  font-family: ${({ theme }) => theme.font.family.primary};
  font-size: ${({ theme }) => theme.font.size.medium};
  font-weight: ${({ theme }) => theme.font.weight.regular};
  line-height: 1.5;

  & p {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  & a {
    color: ${({ theme }) => theme.color.dark};
    font-weight: ${({ theme }) => theme.font.weight.bold};

    &:hover {
      color: ${({ theme }) => theme.color.secondary};
    }
  }
`

const TextFourColumns: React.FC<TextProps> = ({ fields }) => {
  const rawColumns = fields.columns?.slice(0, 4) ?? []
  const columns = rawColumns.filter((col) => {
    const t = col?.title && String(col.title).trim()
    const d = col?.description && String(col.description).trim()
    return Boolean(t || d)
  })

  const hasTitle = Boolean(fields.title && String(fields.title).trim())

  if (!hasTitle && columns.length === 0) {
    return null
  }

  return (
    <Section>
      <div className="container py-3 py-lg-5">
        <div className="row justify-content-center">
          <div className="col-12">
            {hasTitle ? <MainTitle>{fields.title}</MainTitle> : null}

            {columns.length > 0 ? (
              <div className="row g-4">
                {columns.map((col) => (
                  <div
                    key={[col?.title, col?.description].join('::')}
                    className="col-12 col-md-6 col-lg-4 col-xl-3"
                  >
                    <Card>
                      {col?.title && String(col.title).trim() ? (
                        <CardTitle>{col.title}</CardTitle>
                      ) : null}
                      {col?.description && String(col.description).trim() ? (
                        <CardBody content={col.description} />
                      ) : null}
                    </Card>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Section>
  )
}

export default TextFourColumns
